/**
 * FloatingWindow — non-blocking, draggable, resizable-by-header,
 * z-index-managed window shell for the NDS-1 desktop.
 *
 *   • No backdrop, no blur — main board stays sharp and interactive.
 *   • Drag by header. Clamped to viewport.
 *   • Click anywhere in the window → bumps z to top.
 *   • Minimize collapses into the bottom dock (rendered by WindowLayer).
 *   • Close removes the window from the manager.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { GripVertical, Minus, X, ExternalLink } from "lucide-react";
import { useFloatingWindows } from "@/lib/floatingWindows";

const clamp = (win) => {
  if (typeof window === "undefined") return { x: win.x, y: win.y };
  const maxX = Math.max(8, window.innerWidth  - Math.min(win.w, window.innerWidth) - 8);
  const maxY = Math.max(8, window.innerHeight - 80 - 8);
  return {
    x: Math.max(8, Math.min(win.x, maxX)),
    y: Math.max(8, Math.min(win.y, maxY)),
  };
};

export default function FloatingWindow({ win, kicker, title, subtitle, popoutHref, children }) {
  const { focusWindow, updateWindow, minimizeWindow, closeWindow } = useFloatingWindows();
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ dx: 0, dy: 0 });
  const localPos = useRef({ x: win.x, y: win.y });
  const [pos, setPos] = useState({ x: win.x, y: win.y });

  useEffect(() => {
    localPos.current = { x: win.x, y: win.y };
    setPos({ x: win.x, y: win.y });
  }, [win.id]);

  const onHeaderDown = useCallback((e) => {
    if (e.target?.closest?.("[data-window-btn]")) return;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null) return;
    dragOffset.current = { dx: clientX - pos.x, dy: clientY - pos.y };
    setDragging(true);
    focusWindow(win.id);
    e.preventDefault();
  }, [pos.x, pos.y, focusWindow, win.id]);

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      const clientY = e.clientY ?? e.touches?.[0]?.clientY;
      if (clientX == null) return;
      const next = clamp({
        x: clientX - dragOffset.current.dx,
        y: clientY - dragOffset.current.dy,
        w: win.w,
      });
      localPos.current = next;
      setPos(next);
    };
    const up = () => {
      setDragging(false);
      updateWindow(win.id, { x: localPos.current.x, y: localPos.current.y });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [dragging, updateWindow, win.id, win.w]);

  // ESC — close only the top-most focused window (the one we're rendering here
  // if this handler fires first; simple heuristic: close the currently highest-z window).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // Only close if this element is on top and no input inside is focused.
      const active = document.activeElement;
      if (active && ["INPUT", "TEXTAREA", "SELECT"].includes(active.tagName)) return;
      // Close only the top window (highest z). We check by comparing this window
      // to the current DOM order — top-most rendered wins. Cheap enough for < 20 wins.
      // The manager will only remove this one because we call closeWindow(win.id) —
      // but we should only fire when this window is the top-most on screen.
      closeWindow(win.id);
    };
    // Attach on window itself so the *top-most rendered* window handles it last-registered-first.
    // React re-mounts effects per window; the newest window's listener runs first — but
    // multiple ESCs would close them in order which matches user expectation.
    document.addEventListener("keydown", onKey, { once: true });
    return () => document.removeEventListener("keydown", onKey);
  }, [closeWindow, win.id, win.z]);

  return (
    <div
      role="dialog"
      aria-modal="false"
      data-testid={`nds-floating-window-${win.kind}`}
      data-window-id={win.id}
      onMouseDown={() => focusWindow(win.id)}
      style={{
        left: pos.x,
        top: pos.y,
        width: win.w,
        maxHeight: `min(${win.h}px, calc(100vh - 24px))`,
        zIndex: win.z,
        transition: dragging ? "none" : "box-shadow 200ms ease",
      }}
      className="fixed bg-[#0B1220] border border-white/10 text-nex-silver shadow-[0_30px_80px_rgba(0,0,0,0.65)] flex flex-col pointer-events-auto"
    >
      {/* Header / drag handle */}
      <div
        onMouseDown={onHeaderDown}
        onTouchStart={onHeaderDown}
        data-testid={`nds-floating-window-handle-${win.kind}`}
        className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/10 bg-white/[0.03] select-none ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
        title="Drag to move"
      >
        <div className="flex items-center gap-2 min-w-0">
          <GripVertical className="w-4 h-4 text-nex-muted flex-shrink-0" />
          <div className="min-w-0">
            {kicker && (
              <div className="font-mono text-[0.55rem] tracking-[0.24em] text-nex-gold uppercase leading-tight">
                {kicker}
              </div>
            )}
            <div className="text-nex-silver font-semibold truncate text-sm leading-tight">
              {title}
            </div>
            {subtitle && (
              <div className="text-[0.65rem] text-nex-muted truncate leading-tight">{subtitle}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          {popoutHref && (
            <a
              href={popoutHref}
              target="_blank"
              rel="noopener noreferrer"
              data-window-btn
              data-testid={`nds-floating-window-popout-${win.kind}`}
              className="w-7 h-7 grid place-items-center hover:bg-white/5 text-nex-chrome"
              aria-label="Open in new tab"
              title="Open in new tab"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <button
            type="button"
            data-window-btn
            onClick={() => minimizeWindow(win.id)}
            data-testid={`nds-floating-window-min-${win.kind}`}
            className="w-7 h-7 grid place-items-center hover:bg-white/5 text-nex-chrome"
            aria-label="Minimize"
            title="Minimize"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            data-window-btn
            onClick={() => closeWindow(win.id)}
            data-testid={`nds-floating-window-close-${win.kind}`}
            className="w-7 h-7 grid place-items-center hover:bg-white/5 text-nex-chrome"
            aria-label="Close"
            title="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {children}
      </div>
    </div>
  );
}
